/**
 * Device permissions / capabilities — ported from v1.
 *
 * Collects push notification, alarm, and local notification permission status.
 * Also collects background execution diagnostics.
 *
 * On web these return safe defaults via the Notification API.
 * On native (Capacitor) these use NativeBridge for real OS-level permission checks.
 */

import { Capacitor } from "@capacitor/core"
import type { DevicePermissionValue } from "@/db/schema"
import { NativeBridge } from "@/shared/plugins/NativeBridge"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CapabilitySnapshot {
  pushPermission: DevicePermissionValue
  alarmPermission: DevicePermissionValue
  localNotificationPermission: DevicePermissionValue
}

export interface BackgroundExecutionDiagnostics {
  notificationsEnabled?: boolean
  androidCanScheduleExactAlarms?: boolean
  androidIsBackgroundRestricted?: boolean
  iosBackgroundRefreshStatus?: "available" | "denied" | "restricted" | "unknown"
}

export type BackgroundDiagnosticsSnapshot = {
  notificationsEnabled?: 0 | 1
  androidCanScheduleExactAlarms?: 0 | 1
  androidIsBackgroundRestricted?: 0 | 1
  iosBackgroundRefreshStatus?: "available" | "denied" | "restricted" | "unknown"
} | null

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const mapBool01 = (value?: boolean): 0 | 1 | undefined => {
  if (value === true) return 1
  if (value === false) return 0
  return undefined
}

export function toBackgroundDiagnosticsSnapshot(
  diagnostics: BackgroundExecutionDiagnostics | null,
): BackgroundDiagnosticsSnapshot {
  if (!diagnostics) return null
  return {
    notificationsEnabled: mapBool01(diagnostics.notificationsEnabled),
    androidCanScheduleExactAlarms: mapBool01(
      diagnostics.androidCanScheduleExactAlarms,
    ),
    androidIsBackgroundRestricted: mapBool01(
      diagnostics.androidIsBackgroundRestricted,
    ),
    iosBackgroundRefreshStatus: diagnostics.iosBackgroundRefreshStatus,
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Snapshot of device capability permissions.
 * On web: checks Notification API if available, else returns "unknown".
 * On native: uses NativeBridge for OS-level permission checks.
 */
export async function getDeviceCapabilitySnapshot(): Promise<CapabilitySnapshot> {
  if (Capacitor.isNativePlatform()) {
    try {
      const [pushResult, alarmResult] = await Promise.all([
        NativeBridge.getNotificationPermissionStatus(),
        NativeBridge.getAlarmPermissionStatus(),
      ])
      return {
        pushPermission: pushResult.status as DevicePermissionValue,
        alarmPermission: alarmResult.status as DevicePermissionValue,
        // Local notification permission tracks push on both platforms
        localNotificationPermission: pushResult.status as DevicePermissionValue,
      }
    } catch (e) {
      console.warn("[devicePermissions] NativeBridge snapshot failed:", e)
    }
  }

  // Web fallback
  let pushPermission: DevicePermissionValue = "unknown"
  if (typeof Notification !== "undefined") {
    const perm = Notification.permission
    if (perm === "granted") pushPermission = "granted"
    else if (perm === "denied") pushPermission = "denied"
  }

  return {
    pushPermission,
    alarmPermission: "unknown",
    localNotificationPermission: "unknown",
  }
}

/**
 * Request push notification permission.
 * On native: uses NativeBridge.requestNotificationPermission().
 * On web: uses Notification.requestPermission().
 */
export async function requestPushPermission(): Promise<DevicePermissionValue> {
  if (Capacitor.isNativePlatform()) {
    try {
      const result = await NativeBridge.requestNotificationPermission()
      return result.status as DevicePermissionValue
    } catch (e) {
      console.warn("[devicePermissions] requestPushPermission failed:", e)
      return "unknown"
    }
  }

  // Web
  if (typeof Notification !== "undefined") {
    try {
      const result = await Notification.requestPermission()
      if (result === "granted") return "granted"
      if (result === "denied") return "denied"
    } catch (e) {
      console.warn("[devicePermissions] Web requestPermission failed:", e)
    }
  }

  return "unknown"
}

/**
 * Request alarm permission (Android exact alarms / iOS AlarmKit).
 * On native: uses NativeBridge.requestAlarmPermission().
 * On web: no-op, returns "unknown".
 */
export async function requestAlarmPermission(): Promise<DevicePermissionValue> {
  if (Capacitor.isNativePlatform()) {
    try {
      const result = await NativeBridge.requestAlarmPermission()
      return result.status as DevicePermissionValue
    } catch (e) {
      console.warn("[devicePermissions] requestAlarmPermission failed:", e)
      return "unknown"
    }
  }

  return "unknown"
}

/**
 * Background execution diagnostics.
 * On web: returns basic notification status.
 * On native: returns Android exact alarms, background restrictions, iOS background refresh.
 */
export async function getBackgroundExecutionDiagnostics(): Promise<BackgroundExecutionDiagnostics | null> {
  if (Capacitor.isNativePlatform()) {
    try {
      const status = await NativeBridge.getBackgroundExecutionStatus()
      return {
        notificationsEnabled: status.notificationsEnabled,
        androidCanScheduleExactAlarms: status.androidCanScheduleExactAlarms,
        androidIsBackgroundRestricted: status.androidIsBackgroundRestricted,
        iosBackgroundRefreshStatus: status.iosBackgroundRefreshStatus,
      }
    } catch (e) {
      console.warn(
        "[devicePermissions] getBackgroundExecutionDiagnostics failed:",
        e,
      )
    }
  }

  // Web: basic notification check
  if (typeof Notification !== "undefined") {
    return {
      notificationsEnabled: Notification.permission === "granted",
    }
  }

  return null
}

/**
 * Open the OS settings page for background execution / app settings.
 * On native: uses NativeBridge. On web: no-op.
 */
export async function openBackgroundExecutionSettings(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      const result = await NativeBridge.openBackgroundExecutionSettings()
      return result.opened
    } catch (e) {
      console.warn(
        "[devicePermissions] openBackgroundExecutionSettings failed:",
        e,
      )
    }
  }
  return false
}

/**
 * Request Android battery optimization exemption.
 * On native Android: opens system dialog. On iOS/web: no-op.
 */
export async function requestIgnoreBatteryOptimizations(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      const result = await NativeBridge.requestIgnoreBatteryOptimizations()
      return result.opened
    } catch (e) {
      console.warn(
        "[devicePermissions] requestIgnoreBatteryOptimizations failed:",
        e,
      )
    }
  }
  return false
}

/**
 * Whether the current platform supports native alarm scheduling.
 * True on iOS/Android, false on web.
 */
export const supportsNativeAlarms: boolean = Capacitor.isNativePlatform()
